import styled, { css } from 'styled-components';
import { getInvertedColor } from '@elfsight/widget-utils';

const CALENDAR_CLASSNAME = 'react-calendar';

export type DatepickerLayoutProps = {
  _size: number;
  _color: string;
  _padding: number;
  _fontSize: number;
  _accentColor: string;
  _cornerRadius: number;
  _borderRadius: number;
  _backgroundColor: string;
  _withShadow: boolean;
  _fontFamily?: string;
};

export const DatepickerLayout = styled.div<DatepickerLayoutProps>`
  .${CALENDAR_CLASSNAME} {
    /* (tile size) * (number of days in a week) */
    width: ${({ _size, _padding }) => _size * 7 + _padding * 2}px;
    max-width: 100%;
    padding: ${({ _padding }) => _padding}px;
    border-radius: ${({ _borderRadius }) => Math.min(_borderRadius, 32)}px;
    background-color: ${({ _backgroundColor }) => _backgroundColor};
    box-sizing: border-box;

    ${({ _withShadow }) =>
      _withShadow &&
      css`
        box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.3);
      `}

    button {
      font-size: ${({ _fontSize }) => _fontSize}px;
      font-family: inherit;
      font-weight: 400;
      line-height: 20px;

      &:enabled:hover {
        cursor: pointer;
      }
    }

    ${({ _fontFamily }) =>
      _fontFamily &&
      css`
        font-family: ${_fontFamily};
      `}
  }

  ${layoutStyles};
`;

type LayoutStylesProp = {
  _size: number;
  _color: string;
  _accentColor: string;
  _fontSize: number;
  _cornerRadius: number;
};

function layoutStyles({
  _color,
  _accentColor,
  _fontSize,
  _size,
  _cornerRadius
}: LayoutStylesProp) {
  const adaptiveColor = getInvertedColor(_accentColor);

  return css`
    button {
      margin: 0;
      outline: none;
      padding: 0;
      border: 0;
    }

    .${CALENDAR_CLASSNAME} {
      &__tile {
        border-radius: ${_cornerRadius}px;
        width: ${_size}px;
        height: ${_size}px;
        color: ${_color};
        line-height: ${_size}px;
        background-color: transparent;
        text-align: center;

        &:enabled:hover,
        &--now:enabled:hover {
          background-color: rgb(from ${_color} r g b / 10%);
        }

        &--now {
          border: 1px solid ${_accentColor};
        }

        &--hasActive,
        &--hasActive:enabled:hover {
          background-color: ${_accentColor};
          color: ${adaptiveColor};
        }

        &--active {
          background-color: ${_accentColor};
          color: ${adaptiveColor};
        }

        &--active:enabled:hover {
          background-color: ${_accentColor};
        }

        &:disabled {
          background-color: transparent;
          color: rgb(from ${_color} r g b / 30%);
        }
      }

      &--selectRange,
      &__tile--hover {
        background-color: transparent;
      }

      &__month-view__days__day--neighboringMonth,
      &__decade-view__years__year--neighboringDecade,
      &__century-view__decades__decade--neighboringCentury {
        color: rgb(from ${_color} r g b / 50%);
      }
      &--selectRange &__tile--hover {
        background-color: transparent;
      }

      &__header {
        padding-top: 0;
        border-bottom: none;
        background: none;
      }

      &__year-view__months__month,
      &__decade-view__years__year,
      &__century-view__decades__decade {
        min-height: ${_size + 20}px;
      }

      &__navigation {
        display: flex;
        height: 32px;
        margin-bottom: 12px;

        button {
          background-color: transparent;
          border-radius: ${_cornerRadius}px;
          min-width: 32px;
          color: ${_color};

          &:disabled {
            color: rgb(from ${_color} r g b / 30%);
          }

          &:enabled:hover {
            background-color: rgb(from ${_color} r g b / 10%);
          }
        }

        &__arrow {
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
        }
      }

      &__month-view__weekdays {
        font-size: ${_fontSize}px;
        height: ${_size}px;
        text-align: center;
        text-transform: uppercase;
        align-items: center;
        color: rgb(from ${_color} r g b / 50%);

        * {
          text-decoration: none;
        }
      }
    }
  `;
}

const Label = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: all;
  height: 100%;
`;

type NavigationLabelProps = {
  label: string;
  view: 'century' | 'decade' | 'year' | 'month';
};

export const NavigationLabel = ({ label, view }: NavigationLabelProps) => {
  return <Label disabled={view === 'century'}>{label}</Label>;
};
